import { Injectable, inject } from '@angular/core';
import { VoiceModuleHandler } from './voice-handler.interface';
import { VoiceIntent, VoiceExecutionResult } from '../models';
import { InventoriesService } from '../../../modules/inventories/services/inventories.service';
import { AuthService } from '../../auth/auth.service';
import { pickVariant } from './response-variants.util';

/** Forme allégée d'un device retourné par `InventoriesService.getMyDevicesForClaim` */
interface MyDevice {
  id: number;
  label: string;
  serial_number: string | null;
  typeLabel: string | null;
}

/**
 * Payload d'insertion `inventory_claims`. SANS `type` : la colonne prend son
 * défaut `'other'` (la commande vocale ne classe pas la demande). Le
 * formulaire manuel `ClaimDialogComponent`, lui, écrit le vrai `type` et le
 * `device_id` depuis MOKA-232 ; la page Demandes permet de reclasser.
 */
interface NewClaimPayload {
  profile_id: string;
  organisation_id: number;
  content: string;
  /** Équipement concerné, quand il a pu être identifié (MOKA-208 P2 #4). */
  device_id?: number;
  created_at: string;
}

/**
 * MOKA-207 Phase 4, Lot 2 : handler vocal du module Inventaire.
 *
 * `inventory.createClaim { description (requis), deviceLabel? }` — contrat figé,
 * déjà en prod côté edge function (commit c38b9fec). `deviceLabel` est
 * optionnel et se résout désormais en clé étrangère `device_id` (migration
 * 20260923140000) quand il correspond à un équipement assigné à
 * l'utilisateur COURANT ; son libellé (+ type/N° série si connus) est aussi
 * injecté en tête de `content`. Sinon la réclamation reste créée (générique),
 * avec une mention explicite que le matériel cité n'a pas été retrouvé — pas
 * d'échec sec, `description` seule suffit à justifier la réclamation.
 */
@Injectable({ providedIn: 'root' })
export class InventoryVoiceHandler implements VoiceModuleHandler {
  private auth = inject(AuthService);
  private inventoriesService = inject(InventoriesService);

  readonly supportedEndpoints = ['inventory.createClaim'];

  async execute(intent: VoiceIntent): Promise<VoiceExecutionResult> {
    switch (intent.endpoint) {
      case 'inventory.createClaim':
        return this.handleCreateClaim(intent);
      default:
        return { success: false, message: `Action inventaire non supportée: ${intent.endpoint}` };
    }
  }

  private async handleCreateClaim(intent: VoiceIntent): Promise<VoiceExecutionResult> {
    const profile = this.auth.profile;
    if (!profile) {
      return { success: false, message: 'Utilisateur non connecté' };
    }

    const organisationId = this.auth.getOrganisationId();
    if (!organisationId) {
      return { success: false, message: 'Organisation introuvable' };
    }

    const description = (intent.params['description'] as string).trim();
    const deviceLabel = (intent.params['deviceLabel'] as string | undefined)?.trim();

    let content = description;
    let matchedDevice: MyDevice | null = null;

    if (deviceLabel) {
      const devices = await this.inventoriesService.getMyDevicesForClaim(profile.id);
      matchedDevice = this.findDeviceByLabel(devices, deviceLabel);

      content = matchedDevice
        ? `Réclamation vocale concernant ${matchedDevice.label}${this.deviceDetails(matchedDevice)}\n\n${description}`
        : `Réclamation vocale (matériel "${deviceLabel}" non trouvé parmi vos équipements assignés)\n\n${description}`;
    }

    const claim: NewClaimPayload = {
      profile_id: profile.id,
      organisation_id: organisationId,
      content,
      created_at: new Date().toISOString()
    };

    // MOKA-208 P2 #4 : le matériel est désormais une vraie clé étrangère, plus
    // seulement une mention dans le texte — on peut retrouver les réclamations
    // d'un équipement. Le libellé reste dans `content` : il porte le détail
    // (numéro de série) et reste lisible si l'équipement est supprimé.
    if (matchedDevice) {
      claim.device_id = matchedDevice.id;
    }

    try {
      await this.inventoriesService.createClaim(claim);
    } catch (error) {
      const msg = error instanceof Error ? error.message : 'erreur inconnue';
      return { success: false, message: `Erreur lors de la création de la réclamation : ${msg}` };
    }

    const unmatchedDevice = !!deviceLabel && !matchedDevice;
    const message = unmatchedDevice
      ? pickVariant([
          `Réclamation envoyée. Matériel "${deviceLabel}" non reconnu parmi vos équipements : réclamation créée sans lien à un appareil précis.`,
          `C'est fait : réclamation créée, mais "${deviceLabel}" ne correspond à aucun de vos équipements assignés.`
        ])
      : pickVariant(
          matchedDevice
            ? [
                `Réclamation envoyée concernant "${matchedDevice.label}"`,
                `C'est fait : réclamation créée pour "${matchedDevice.label}"`
              ]
            : ['Réclamation envoyée', `C'est fait : réclamation créée`]
        );

    return {
      success: true,
      message,
      data: {
        deviceMatched: !!matchedDevice,
        deviceLabel: matchedDevice?.label ?? deviceLabel ?? null,
        description
      }
    };
  }

  private deviceDetails(device: MyDevice): string {
    const parts = [device.typeLabel, device.serial_number ? `N° série: ${device.serial_number}` : null]
      .filter((v): v is string => !!v);
    return parts.length > 0 ? ` (${parts.join(' — ')})` : '';
  }

  /**
   * Résolution floue du libellé dicté vers l'un des équipements ASSIGNÉS à
   * l'utilisateur courant. Même stratégie (exact → inclusion unique →
   * inclusion inverse → plus proche en longueur) que
   * `ProjectVoiceHandler.findTaskByTitle` — réutilisée telle quelle pour
   * rester cohérent avec le seul autre pattern de résolution floue du module
   * vocal.
   */
  private findDeviceByLabel(devices: MyDevice[], label: string): MyDevice | null {
    const normalized = label.toLowerCase();

    const exact = devices.find(d => d.label.toLowerCase() === normalized);
    if (exact) return exact;

    const includes = devices.filter(d => d.label.toLowerCase().includes(normalized));
    if (includes.length === 1) return includes[0];

    const reverse = devices.filter(d => normalized.includes(d.label.toLowerCase()));
    if (reverse.length === 1) return reverse[0];

    if (includes.length > 1) {
      includes.sort((a, b) =>
        Math.abs(a.label.length - label.length) - Math.abs(b.label.length - label.length)
      );
      return includes[0];
    }

    return null;
  }
}
